import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation, withTranslation, type WithTranslation } from 'react-i18next';
import { useHomeDestination } from '@hooks/useHomeDestination';

interface Props extends WithTranslation {
  children: ReactNode;
  /**
   * Changing this discards the error state. `MainLayout` passes the current
   * pathname, so navigating away from a broken page recovers instead of
   * pinning the error UI over every subsequent route.
   */
  resetKey?: string;
}

interface State {
  error: Error | null;
}

/**
 * The fallback's way out. A function component so it can read the session:
 * public pages (help, legal) render inside this boundary, and "/" is a
 * signed-in route (REV-939), so signed out it goes to sign-in and says so.
 */
function WayOutLink() {
  const { t } = useTranslation();
  const home = useHomeDestination();
  return (
    <Button component={RouterLink} to={home.to} variant="outlined">
      {home.signedIn ? t('errors.boundary.home', 'Go to home') : t('common.signIn', 'Sign in')}
    </Button>
  );
}

/**
 * REV-588: stops a render-time throw from unmounting the whole app.
 *
 * React's default on an uncaught render error is to unmount the entire tree,
 * which left a literally empty document — no <main>, no message, no way back.
 * Four real pages did this (SeatUsageCard, ContextsTab, TeamsTab and the
 * remediation plan detail page), every one of them from a 200 response whose
 * body shape differed from what the component read off it.
 *
 * Must be a class: `componentDidCatch` and `getDerivedStateFromError` have no
 * hook equivalent.
 */
class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Deliberately console.error, not a logger call: this file must not import
    // anything that could itself throw, or the boundary takes the app down
    // with it. No user data is included — the message and stack are ours.
    console.error('Unhandled render error:', error, info.componentStack);
  }

  handleRetry = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    const { children, t } = this.props;

    if (!error) return children;

    return (
      <Paper
        variant="outlined"
        // role=alert so the failure is announced. A blank document told a
        // screen-reader user nothing at all, which is worse than a visible
        // error: there was no signal that anything had happened.
        role="alert"
        sx={{ p: 3, mt: 2 }}
      >
        <Typography variant="h2" sx={{ fontSize: '1.5rem', fontWeight: 600, mb: 1 }}>
          {t('errors.boundary.title', 'This page did not load')}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {t(
            'errors.boundary.message',
            'Something went wrong while displaying this page. The rest of the app still works.'
          )}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button variant="contained" onClick={this.handleRetry}>
            {t('errors.boundary.retry', 'Try again')}
          </Button>
          <WayOutLink />
        </Box>
        {import.meta.env.DEV && (
          <Box component="pre" sx={{ mt: 2, overflowX: 'auto', fontSize: '0.75rem' }}>
            {error.message}
          </Box>
        )}
      </Paper>
    );
  }
}

export default withTranslation()(ErrorBoundary);
